import React, { useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  exists,
  gradingPeriodLengthsProps,
  isUserRole,
} from "@examspark/common";
import {
  formatLongDate,
  fromAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  Breadcrumbs,
  NoResults,
  usePaginatedSearchCourseCalendarsByCourse,
  useQueryGetCoursesBySubject,
  useQueryGetDefaultWorkspaceCalendar,
  useQueryListAcademicCalendars,
  useAppContext,
  useQueryListContentSharedToWorkspace,
} from "@examspark/common-app";
import { subjectMeta } from "@examspark/common-meta";
import {
  CheckboxInput,
  FlexBox,
  Loading,
  RouterLink,
  SearchField,
  SelectInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import Button, {
  LinkButton,
} from "@examspark/common-ui/dist/components/buttons/Button";
import {
  IconAddLine,
  IconPageNextLine,
  IconPagePreviousLine,
  IconRefreshLine,
  IconRemoveLine,
  IconSearchLine,
} from "@examspark/common-ui/dist/components/icons";
import {
  Label,
  LabelLike,
} from "@examspark/common-ui/dist/components/inputs/Label";
import { BetterTabs } from "@examspark/common-ui/dist/components/tabs/Tabs";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";
import { Course, WorkspaceUserRole } from "@examspark/types";

import { useFlags } from "../../hooks/useFlags";
import BrowseList from "../browse/BrowseList";
import { ResultFooter } from "../browse/ItemMetadata";
import PageHead from "../common/PageHead";
import PageTitle from "../common/PageTitle";

const StyledArchiveLabel = styled(Label)`
  [type="checkbox"] {
    order: -1;
  }
`;

export const ManageCalendars = () => {
  const [context] = useAppContext();
  const [tab, setTab] = useState<"academic" | "course">("academic");
  const match = useMatch("/calendar/:tab");
  const navigate = useNavigate();

  useEffect(() => {
    if (
      match &&
      match.params.tab &&
      ["academic", "course"].includes(match.params.tab)
    ) {
      setTab(match.params.tab as "academic" | "course");
    }
  }, [match]);

  const flags = useFlags();

  const canManageCalendars = isUserRole(
    context.workspaceUser,
    WorkspaceUserRole.ContentManager,
  );

  if (!canManageCalendars || !flags.showCalendarManagement) {
    return <NoResults>You do not have permission to view this page.</NoResults>;
  }

  return (
    <VSpace size="3rem">
      <PageHead>
        <Breadcrumbs
          items={[
            ...Breadcrumbs.Dashboard(),
            <RouterLink key="workspace" to="/workspace">
              Workspace
            </RouterLink>,
            <RouterLink key="workspace/calendars" to="/workspace/calendars">
              Calendars
            </RouterLink>,
          ]}
        />
        <PageTitle>Manage Calendars</PageTitle>
      </PageHead>
      <BetterTabs
        activeTab={tab}
        onTabChange={(tab) => navigate(`/calendar/${tab}`)}
        tabs={[
          {
            tab: "academic",
            label: "Academic Calendars",
            element: <ManageAcademicCalendars />,
          },
          {
            tab: "course",
            label: "Course Calendars",
            element: <ManageCourseCalendars />,
          },
        ]}
      />
    </VSpace>
  );
};

const ManageAcademicCalendars = () => {
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [searchArchived, setSearchArchived] = useState<boolean>(false);
  const [context] = useAppContext();
  const academicCalendarQuery = useQueryListAcademicCalendars(
    context.workspaceUser?.workspace?.id,
    { includeArchived: searchArchived },
  );
  const defaultCalendarQuery = useQueryGetDefaultWorkspaceCalendar(
    context.workspaceUser?.workspace?.id,
  );

  const { data: sharedContent } = useQueryListContentSharedToWorkspace();

  const workspaceId = context.workspaceUser?.workspace?.id;

  const allCalendars = academicCalendarQuery.data
    ?.map((calendar) => ({
      ...calendar,
      // we didn't give a name to the bootstrapped calendars, so patch that
      name: calendar.name ?? "(Untitled Calendar)",
    }))
    .filter(
      (calendar) =>
        calendar.name?.length &&
        calendar.name?.toLowerCase().includes(searchFilter.toLowerCase()),
    )
    ?.sort((a, b) => {
      const aIsSameWorkspace = a.workspaceId === workspaceId ? 1 : 0;
      const bIsSameWorkspace = b.workspaceId === workspaceId ? 1 : 0;

      return (
        bIsSameWorkspace - aIsSameWorkspace ||
        (a.name ?? "").localeCompare(b.name ?? "")
      );
    });

  return academicCalendarQuery.isLoading ? (
    <Loading />
  ) : academicCalendarQuery.isError ? (
    <>Error: failed to load academic calendars</>
  ) : (
    <>
      <Toolbar>
        <SearchField
          placeholder="Filter Calendars"
          width={"100%"}
          onInput={(e) => setSearchFilter(e.currentTarget.value)}
        />
        <StyledArchiveLabel layout="row" label="Show Archived Calendars">
          <CheckboxInput
            checked={searchArchived}
            onChange={(e) => setSearchArchived(e.currentTarget.checked)}
          />
        </StyledArchiveLabel>
        <Button
          title="Reload"
          onClick={() => academicCalendarQuery.refetch()}
          icon={<IconRefreshLine />}
          isLoading={academicCalendarQuery.isFetching}
        />
        <LinkButton
          icon={<IconAddLine />}
          color="create"
          to={"/calendar/workspace/new"}
        >
          Academic Calendar
        </LinkButton>
      </Toolbar>
      <BrowseList
        data={allCalendars}
        hideBankBadge
        itemMetadata={(item) => (
          <ResultFooter>
            <div>
              {formatLongDate(fromAWSDate(item.startAt))} -{" "}
              {formatLongDate(fromAWSDate(item.endAt))}
            </div>
            {!!item.gradingPeriodLength && (
              <div>
                {gradingPeriodLengthsProps[item.gradingPeriodLength]?.label}
              </div>
            )}
            {!!item.isDeleted && <div>DELETED</div>}
            {defaultCalendarQuery.data?.id === item.id && (
              <div>Default Calendar</div>
            )}
            {item.workspaceId !== context.workspaceUser?.workspace?.id && (
              <div>
                Shared by:{" "}
                {
                  sharedContent
                    ?.map((share) => share.workspace)
                    ?.filter(exists)
                    .find((w) => w.id === item.workspaceId)?.name
                }
              </div>
            )}
            {item.isArchived && <div>Archived</div>}
          </ResultFooter>
        )}
      />
    </>
  );
};

const ManageCourseCalendars = () => {
  const [subject, setSubject] = useState<string>();
  const [course, setCourse] = useState<Course>();
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [searchArchived, setSearchArchived] = useState<boolean>(false);
  const [context] = useAppContext();

  const courseQuery = useQueryGetCoursesBySubject(subject);
  const { data: sharedContent } = useQueryListContentSharedToWorkspace();
  // const courseCalendarQuery = useQueryListCourseCalendarsByCourse(course);
  const debouncedSearchFilter = useDebounce(searchFilter, 1000);
  const search = usePaginatedSearchCourseCalendarsByCourse(
    course?.id,
    debouncedSearchFilter,
    searchArchived,
  );
  const searchData = search.currentData?.items;
  const calendars = searchData?.filter((calendar) => true) ?? [];

  return (
    <FlexBox style={{ gap: "2rem" }}>
      <Toolbar>
        <SearchField
          placeholder="Filter Calendars"
          width={"100%"}
          value={searchFilter}
          onInput={(e) => setSearchFilter(e.currentTarget.value)}
          onEnterKeyPress={() => search.refetch()}
        />
        <Button
          title="Reload"
          onClick={() => search.refetch()}
          icon={<IconSearchLine />}
          isLoading={search.isFetching}
        >
          Search
        </Button>
        <Button
          title="Reset"
          icon={<IconRemoveLine />}
          onClick={() => {
            setSubject(undefined);
            setCourse(undefined);
            setSearchFilter("");
          }}
        >
          Clear
        </Button>
        <LinkButton
          icon={<IconAddLine />}
          color="create"
          to={"/calendar/course/new"}
        >
          Course Calendar
        </LinkButton>
      </Toolbar>
      <LabelLike label="Filter by Course/Subject. Both course and subject must be selected for filter to operate">
        <Toolbar stretch>
          <Toolbar.Flex>
            <SelectInput
              value={subject}
              placeholder="Select Subject"
              options={Object.entries(subjectMeta)
                .filter(([key]) => key !== "CTE")
                .map(([key, { label }]) => ({
                  label,
                  value: key,
                }))}
              isClearable
              onChange={(option) => setSubject(option?.value)}
            />
          </Toolbar.Flex>
          <Toolbar.Flex>
            <SelectInput
              disabled={!courseQuery.isFetched}
              value={course?.id}
              placeholder="Select Course"
              options={courseQuery.data
                ?.sort(
                  (a, b) =>
                    +(a?.sequence ?? 0) - +(b?.sequence ?? 0) ||
                    (a?.name ?? "").localeCompare(b?.name ?? ""),
                )
                ?.map((course) => ({
                  label: course.name ?? "",
                  value: course.id,
                  course,
                }))}
              isClearable
              onChange={(option) => setCourse(option?.course)}
            />
          </Toolbar.Flex>
        </Toolbar>
      </LabelLike>

      <div>
        <StyledArchiveLabel layout="row" label="Show Archived Calendars">
          <CheckboxInput
            checked={searchArchived}
            onChange={(e) => setSearchArchived(e.currentTarget.checked)}
          />
        </StyledArchiveLabel>
      </div>

      <Toolbar>
        <Button
          title=""
          onClick={search.previousPage}
          variant="plain"
          disabled={!search.canPagePrevious}
        >
          <IconPagePreviousLine width="10" />
          Prev
        </Button>
        <Toolbar.Flex />
        <div>Page {search.currentPage}</div>
        <Toolbar.Flex />
        <Button
          title=""
          onClick={search.nextPage}
          disabled={!search.canPageNext}
          variant="plain"
        >
          Next
          <IconPageNextLine width="10" />
        </Button>
      </Toolbar>

      {search.isLoading ? (
        <Loading />
      ) : search.isError ? (
        <>Error: Unable to list calendars</>
      ) : (
        <BrowseList
          data={calendars}
          hideBankBadge
          itemMetadata={(item) => (
            <ResultFooter>
              {!!item.calendar && (
                <div title={item.calendar.id}>
                  {item.calendar?.name ?? "Untitled Calendar"}
                  {item.calendar.isDeleted && (
                    <>
                      {" "}
                      - <span style={{ color: "red" }}>Deleted</span>
                    </>
                  )}
                </div>
              )}
              {!!item.calendar?.gradingPeriodLength && (
                <div>
                  {
                    gradingPeriodLengthsProps[item.calendar.gradingPeriodLength]
                      ?.label
                  }
                </div>
              )}
              {!!item.course && <div>{item.course?.name}</div>}
              {item.workspaceId !== context.workspaceUser?.workspace?.id && (
                <div>
                  Shared by:{" "}
                  {
                    sharedContent
                      ?.map((share) => share.workspace)
                      ?.filter(exists)
                      .find((w) => w.id === item.workspaceId)?.name
                  }
                </div>
              )}
              {item.isArchived && <div>Archived</div>}
            </ResultFooter>
          )}
        />
      )}
    </FlexBox>
  );
};
